import type { RefObject } from 'react';
import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl';
import { referencePaintQuery } from '../../../core/mapV2/queries';
import { useAppSelector } from '../../../types';
import { ROAD_SECTIONS_SOURCE_ID } from './sources';

// Sections colored per animation frame: a large network (30,000 sections) must not freeze the page.
export const COLOR_CHUNK_SIZE = 2000;

const target = { source: ROAD_SECTIONS_SOURCE_ID, sourceLayer: ROAD_SECTIONS_SOURCE_ID };

// Paints the référentiel: every section gets a `color` feature-state (its indicator color) and a `dim` one
// (left out by the filters), which the layer reads. Feature-state belongs to the source, not to its tiles, so it follows tiles as they load; it is
// only lost when the source itself is replaced (basemap change), which is detected below.
export const useReferenceColoring = (mapRef: RefObject<MapRef | null>, loaded: boolean) => {
    const paint = useAppSelector(referencePaintQuery);

    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!loaded || !map) {
            return undefined;
        }
        let run = 0;
        let frame = 0;
        let paintedSource: unknown = null;

        const apply = () => {
            const source = map.getSource(ROAD_SECTIONS_SOURCE_ID);
            if (!source) {
                return;
            }
            const current = ++run;
            cancelAnimationFrame(frame);
            paintedSource = source;
            map.removeFeatureState(target, 'color');
            map.removeFeatureState(target, 'dim');
            if (!paint) {
                return;
            }
            const entries = Array.from(paint.entries());
            let index = 0;
            const step = () => {
                if (current !== run) {
                    return;
                }
                const end = Math.min(index + COLOR_CHUNK_SIZE, entries.length);
                for (; index < end; index += 1) {
                    const [id, state] = entries[index] ?? [];
                    if (id !== undefined && state !== undefined) {
                        map.setFeatureState({ ...target, id }, { color: state.color, dim: state.dim });
                    }
                }
                if (index < entries.length) {
                    frame = requestAnimationFrame(step);
                }
            };
            step();
        };
        apply();

        const onSourceData = (event: { sourceId?: string }) => {
            if (
                event.sourceId === ROAD_SECTIONS_SOURCE_ID &&
                map.getSource(ROAD_SECTIONS_SOURCE_ID) !== paintedSource
            ) {
                apply();
            }
        };
        map.on('sourcedata', onSourceData);

        return () => {
            run += 1;
            cancelAnimationFrame(frame);
            map.off('sourcedata', onSourceData);
        };
    }, [mapRef, loaded, paint]);
};
